import { formatTimeFromHHmm, formatTimeRange, getLocalDateKey } from "utils/dateTime";
import {
  normalizeCountdownEventCount,
  normalizeTeacherHubCountdownDisplayLabels,
  normalizeTeacherHubCountdownEventIds,
} from "utils/countdownEventTargets";

const EVENT_SCHEMA_VERSION = 2;

export const TEACHER_HUB_EVENT_SCOPES = [
  { value: "school", label: "School-wide" },
  { value: "class", label: "Specific Classes" },
  { value: "personal", label: "Personal" },
];

export const TEACHER_HUB_EVENT_STATUSES = [
  { value: "active", label: "Active" },
  { value: "cancelled", label: "Cancelled" },
  { value: "hidden", label: "Hidden" },
];

export const TEACHER_HUB_RECURRENCE_FREQUENCIES = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const VALID_SCOPES = new Set(TEACHER_HUB_EVENT_SCOPES.map((option) => option.value));
const VALID_STATUSES = new Set(TEACHER_HUB_EVENT_STATUSES.map((option) => option.value));
const VALID_FREQUENCIES = new Set(TEACHER_HUB_RECURRENCE_FREQUENCIES.map((option) => option.value));
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const createFallbackId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const createTeacherHubEventId = () =>
  `event-${typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : createFallbackId()}`;

export const getTodayDateKey = () => getLocalDateKey();

export const DEFAULT_TEACHER_HUB_EVENT = {
  schemaVersion: EVENT_SCHEMA_VERSION,
  id: "",
  title: "",
  category: "",
  tags: [],
  scope: "school",
  classId: "",
  classIds: [],
  ownerType: "teacher",
  ownerId: "",
  status: "active",
  featured: false,
  priority: 0,
  startDate: "",
  endDate: "",
  allDay: true,
  startTime: "",
  endTime: "",
  notes: "",
  recurrence: {
    enabled: false,
    frequency: "none",
    interval: 1,
    until: "",
    count: null,
    daysOfWeek: [],
    exceptions: [],
  },
  scheduleReviewHint: {
    enabled: false,
    scheduleTemplateId: "",
    promptLabel: "",
    notes: "",
  },
};

const toDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const dateKeyToDate = (dateKey) => {
  const [year, month, day] = String(dateKey || "").split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  return new Date(year, month - 1, day);
};

const addDays = (dateKey, days) => {
  const date = dateKeyToDate(dateKey);
  if (!date) return "";
  date.setDate(date.getDate() + days);
  return toDateKey(date);
};

const enumerateDateKeys = (startDateKey, endDateKey) => {
  const startDate = dateKeyToDate(startDateKey);
  const endDate = dateKeyToDate(endDateKey);
  if (!startDate || !endDate) return [];

  const keys = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    keys.push(toDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
};

const dayDiff = (startDateKey, endDateKey) => {
  const startDate = dateKeyToDate(startDateKey);
  const endDate = dateKeyToDate(endDateKey);
  if (!startDate || !endDate) return 0;
  return Math.round((endDate.getTime() - startDate.getTime()) / DAY_MS);
};

const monthDiff = (startDateKey, endDateKey) => {
  const startDate = dateKeyToDate(startDateKey);
  const endDate = dateKeyToDate(endDateKey);
  if (!startDate || !endDate) return 0;
  return (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
};

const yearDiff = (startDateKey, endDateKey) => {
  const startDate = dateKeyToDate(startDateKey);
  const endDate = dateKeyToDate(endDateKey);
  if (!startDate || !endDate) return 0;
  return endDate.getFullYear() - startDate.getFullYear();
};

const normalizeText = (value) => (typeof value === "string" ? value.trim() : "");

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeTags = (value) => {
  if (Array.isArray(value)) {
    return Array.from(new Set(value.map((item) => normalizeText(item)).filter(Boolean)));
  }
  if (typeof value === "string") {
    return Array.from(
      new Set(
        value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      )
    );
  }
  return [];
};

export const normalizeTeacherHubEventClassIds = (rawEvent = {}) => {
  const fromArray = normalizeArray(rawEvent.classIds)
    .map((item) => normalizeText(item))
    .filter(Boolean);
  if (fromArray.length) {
    return Array.from(new Set(fromArray));
  }

  const legacyClassId = normalizeText(rawEvent.classId);
  return legacyClassId ? [legacyClassId] : [];
};

export const getTeacherHubEventClassIds = (eventItem = {}) =>
  normalizeTeacherHubEventClassIds(eventItem);

export const teacherHubEventAppliesToClass = (eventItem, classId) => {
  if (!eventItem || !classId) return false;
  if (eventItem.scope !== "class") return false;
  return getTeacherHubEventClassIds(eventItem).some(
    (eventClassId) => String(eventClassId) === String(classId)
  );
};

const normalizeDaysOfWeek = (value, fallbackDateKey) => {
  const dayKeys = normalizeArray(value)
    .map((item) => String(item || "").toLowerCase().slice(0, 3))
    .filter((item) => WEEKDAY_KEYS.includes(item));

  if (dayKeys.length) {
    return Array.from(new Set(dayKeys)).sort((leftDay, rightDay) => WEEKDAY_KEYS.indexOf(leftDay) - WEEKDAY_KEYS.indexOf(rightDay));
  }

  const fallbackDate = dateKeyToDate(fallbackDateKey);
  return fallbackDate ? [WEEKDAY_KEYS[fallbackDate.getDay()]] : [];
};

const normalizeRecurrence = (rawRecurrence, startDate) => {
  const recurrence = rawRecurrence && typeof rawRecurrence === "object" ? rawRecurrence : {};
  const rawFrequency = normalizeText(recurrence.frequency || (recurrence.enabled ? "weekly" : "none")).toLowerCase();
  const frequency = VALID_FREQUENCIES.has(rawFrequency) ? rawFrequency : "none";
  const enabled = Boolean(recurrence.enabled) && frequency !== "none";
  const intervalValue = Number(recurrence.interval);
  const interval = Number.isFinite(intervalValue) && intervalValue > 0 ? Math.floor(intervalValue) : 1;
  const until = normalizeText(recurrence.until);
  const normalizedUntil = until && (!startDate || until >= startDate) ? until : "";
  const countValue = Number(recurrence.count);
  const count = Number.isFinite(countValue) && countValue > 0 ? Math.floor(countValue) : null;

  return {
    enabled,
    frequency: enabled ? frequency : "none",
    interval,
    until: normalizedUntil,
    count,
    daysOfWeek: enabled && frequency === "weekly" ? normalizeDaysOfWeek(recurrence.daysOfWeek, startDate) : [],
    exceptions: Array.from(
      new Set(normalizeArray(recurrence.exceptions).map((item) => normalizeText(item)).filter(Boolean))
    ).sort(),
  };
};

const normalizeScheduleReviewHint = (rawHint, legacyScheduleTemplateId) => {
  const hint = rawHint && typeof rawHint === "object" ? rawHint : {};
  const scheduleTemplateId = normalizeText(hint.scheduleTemplateId || legacyScheduleTemplateId);
  return {
    enabled: Boolean(hint.enabled) || Boolean(scheduleTemplateId),
    scheduleTemplateId,
    promptLabel: normalizeText(hint.promptLabel),
    notes: normalizeText(hint.notes),
  };
};

export const normalizeTeacherHubEvent = (rawEvent = {}, options = {}) => {
  const startDate = normalizeText(rawEvent.startDate || rawEvent.date || options.defaultDate || getTodayDateKey());
  const endDateCandidate = normalizeText(rawEvent.endDate || startDate);
  const endDate = endDateCandidate && endDateCandidate >= startDate ? endDateCandidate : startDate;
  const scope = VALID_SCOPES.has(rawEvent.scope) ? rawEvent.scope : "school";
  const startTime = normalizeText(rawEvent.startTime);
  const endTime = normalizeText(rawEvent.endTime);
  const category = normalizeText(rawEvent.category || rawEvent.type);
  const classIds = scope === "class" ? normalizeTeacherHubEventClassIds(rawEvent) : [];

  return {
    ...DEFAULT_TEACHER_HUB_EVENT,
    ...rawEvent,
    schemaVersion: EVENT_SCHEMA_VERSION,
    id: String(rawEvent.id || createTeacherHubEventId()),
    title: normalizeText(rawEvent.title),
    category,
    tags: normalizeTags(rawEvent.tags),
    scope,
    // Keep legacy classId as the first selected class for older readers.
    classId: classIds[0] || "",
    classIds,
    ownerType: normalizeText(rawEvent.ownerType) || "teacher",
    ownerId: normalizeText(rawEvent.ownerId || options.userId),
    status: VALID_STATUSES.has(rawEvent.status) ? rawEvent.status : "active",
    featured: Boolean(rawEvent.featured),
    priority: Number.isFinite(Number(rawEvent.priority)) ? Number(rawEvent.priority) : 0,
    startDate,
    endDate,
    allDay: typeof rawEvent.allDay === "boolean" ? rawEvent.allDay : !(startTime || endTime),
    startTime,
    endTime,
    notes: normalizeText(rawEvent.notes),
    recurrence: normalizeRecurrence(rawEvent.recurrence, startDate),
    scheduleReviewHint: normalizeScheduleReviewHint(rawEvent.scheduleReviewHint, rawEvent.scheduleTemplateId),
  };
};

export const sortTeacherHubEvents = (events = []) =>
  [...events].sort((leftEvent, rightEvent) => {
    const leftKey = `${leftEvent?.startDate || ""} ${leftEvent?.startTime || "00:00"} ${leftEvent?.title || ""}`;
    const rightKey = `${rightEvent?.startDate || ""} ${rightEvent?.startTime || "00:00"} ${rightEvent?.title || ""}`;
    return leftKey.localeCompare(rightKey);
  });

export const normalizeTeacherHubEvents = (events = [], options = {}) =>
  sortTeacherHubEvents(normalizeArray(events).map((eventItem) => normalizeTeacherHubEvent(eventItem, options)));

export const getTeacherHubEventsFromSettings = (settings = {}, options = {}) =>
  normalizeTeacherHubEvents(
    options.events ?? options.teacherHubEvents ?? settings?.teacherHubEvents ?? [],
    options
  );

export const getTeacherHubEventsForUser = (user, options = {}) =>
  getTeacherHubEventsFromSettings(user?.settings, {
    ...options,
    userId: options.userId ?? user?.uid,
    events: options.events ?? user?.teacherHubEvents,
  });

export const getTeacherHubEventCount = (settings = {}, options = {}) =>
  getTeacherHubEventsFromSettings(settings, options).length;

export const getTeacherHubEventCountForUser = (user, options = {}) =>
  getTeacherHubEventsForUser(user, options).length;

const isVisibleForContext = (eventItem, context = {}) => {
  if (!eventItem) return false;
  const includeSchool = context.includeSchool !== false;
  const includeClass = context.includeClass !== false;
  const includePersonal = Boolean(context.includePersonal);

  if (eventItem.scope === "school") return includeSchool;
  if (eventItem.scope === "personal") return includePersonal;
  if (eventItem.scope === "class") {
    if (!includeClass) return false;
    if (context.includeAllClasses) return true;
    const eventClassIds = getTeacherHubEventClassIds(eventItem);
    if (!eventClassIds.length) return false;
    if (Array.isArray(context.classIds) && context.classIds.length > 0) {
      return context.classIds.some((classId) =>
        eventClassIds.some((eventClassId) => String(eventClassId) === String(classId))
      );
    }
    return Boolean(context.classId)
      && eventClassIds.some((eventClassId) => String(eventClassId) === String(context.classId));
  }
  return false;
};

const matchesRecurrenceRule = (eventItem, candidateDateKey) => {
  const recurrence = eventItem?.recurrence;
  if (!recurrence?.enabled || recurrence.frequency === "none") {
    return candidateDateKey === eventItem.startDate;
  }

  if (candidateDateKey < eventItem.startDate) return false;
  if (recurrence.until && candidateDateKey > recurrence.until) return false;
  if (recurrence.exceptions?.includes(candidateDateKey)) return false;

  if (recurrence.frequency === "daily") {
    return dayDiff(eventItem.startDate, candidateDateKey) % recurrence.interval === 0;
  }

  if (recurrence.frequency === "weekly") {
    const candidateDate = dateKeyToDate(candidateDateKey);
    if (!candidateDate) return false;
    const weekdayKey = WEEKDAY_KEYS[candidateDate.getDay()];
    if (!recurrence.daysOfWeek.includes(weekdayKey)) return false;

    const eventStartDate = dateKeyToDate(eventItem.startDate);
    const eventWeekStart = new Date(eventStartDate);
    const candidateWeekStart = new Date(candidateDate);
    eventWeekStart.setDate(eventWeekStart.getDate() - eventWeekStart.getDay());
    candidateWeekStart.setDate(candidateWeekStart.getDate() - candidateWeekStart.getDay());

    const weeksBetween = Math.round((candidateWeekStart.getTime() - eventWeekStart.getTime()) / (7 * DAY_MS));
    return weeksBetween >= 0 && weeksBetween % recurrence.interval === 0;
  }

  if (recurrence.frequency === "monthly") {
    const candidateDate = dateKeyToDate(candidateDateKey);
    const eventStartDate = dateKeyToDate(eventItem.startDate);
    if (!candidateDate || !eventStartDate) return false;
    if (candidateDate.getDate() !== eventStartDate.getDate()) return false;
    const monthsBetween = monthDiff(eventItem.startDate, candidateDateKey);
    return monthsBetween >= 0 && monthsBetween % recurrence.interval === 0;
  }

  if (recurrence.frequency === "yearly") {
    const candidateDate = dateKeyToDate(candidateDateKey);
    const eventStartDate = dateKeyToDate(eventItem.startDate);
    if (!candidateDate || !eventStartDate) return false;
    if (candidateDate.getMonth() !== eventStartDate.getMonth() || candidateDate.getDate() !== eventStartDate.getDate()) {
      return false;
    }
    const yearsBetween = yearDiff(eventItem.startDate, candidateDateKey);
    return yearsBetween >= 0 && yearsBetween % recurrence.interval === 0;
  }

  return false;
};

export const getTeacherHubEventOccurrences = (events = [], options = {}) => {
  const rangeStart = normalizeText(options.rangeStart || getTodayDateKey());
  const rangeEnd = normalizeText(options.rangeEnd || addDays(rangeStart, 180));
  const normalizedEvents = normalizeTeacherHubEvents(events, options);
  const allowedStatuses = Array.isArray(options.statuses) && options.statuses.length ? new Set(options.statuses) : new Set(["active"]);
  const occurrences = [];

  normalizedEvents.forEach((eventItem) => {
    if (!allowedStatuses.has(eventItem.status)) return;
    if (!isVisibleForContext(eventItem, options)) return;

    const durationDays = Math.max(0, dayDiff(eventItem.startDate, eventItem.endDate));
    const scanStart = eventItem.recurrence.enabled ? eventItem.startDate : addDays(eventItem.startDate, 0);
    const cursorStart = scanStart < addDays(rangeStart, -durationDays) ? addDays(rangeStart, -durationDays) : scanStart;
    const candidateDates = enumerateDateKeys(cursorStart, rangeEnd);

    let matchCount = 0;
    candidateDates.forEach((candidateDateKey) => {
      if (!matchesRecurrenceRule(eventItem, candidateDateKey)) return;
      matchCount += 1;
      if (eventItem.recurrence.count && matchCount > eventItem.recurrence.count) return;

      const occurrenceStartDate = candidateDateKey;
      const occurrenceEndDate = addDays(candidateDateKey, durationDays);
      if (occurrenceEndDate < rangeStart || occurrenceStartDate > rangeEnd) return;

      occurrences.push({
        event: eventItem,
        eventId: eventItem.id,
        title: eventItem.title,
        startDate: occurrenceStartDate,
        endDate: occurrenceEndDate,
        startTime: eventItem.startTime,
        endTime: eventItem.endTime,
        isRecurring: Boolean(eventItem.recurrence.enabled),
      });
    });
  });

  return occurrences.sort((leftOccurrence, rightOccurrence) => {
    const leftKey = `${leftOccurrence.startDate} ${leftOccurrence.startTime || "00:00"} ${leftOccurrence.title || ""}`;
    const rightKey = `${rightOccurrence.startDate} ${rightOccurrence.startTime || "00:00"} ${rightOccurrence.title || ""}`;
    return leftKey.localeCompare(rightKey);
  });
};

export const canSkipTeacherHubEventOccurrence = (eventItem, dateKey) => {
  const occurrenceDateKey = normalizeText(dateKey);
  if (!occurrenceDateKey) return false;
  const normalizedEvent = normalizeTeacherHubEvent(eventItem);
  return Boolean(normalizedEvent.recurrence.enabled) && matchesRecurrenceRule(normalizedEvent, occurrenceDateKey);
};

export const skipTeacherHubEventOccurrence = (eventItem, dateKey) => {
  const occurrenceDateKey = normalizeText(dateKey);
  const normalizedEvent = normalizeTeacherHubEvent(eventItem);
  if (!canSkipTeacherHubEventOccurrence(normalizedEvent, occurrenceDateKey)) return null;

  return {
    ...normalizedEvent,
    recurrence: {
      ...normalizedEvent.recurrence,
      exceptions: Array.from(new Set([...normalizedEvent.recurrence.exceptions, occurrenceDateKey])).sort(),
    },
  };
};

export const restoreTeacherHubEventOccurrence = (eventItem, dateKey) => {
  const occurrenceDateKey = normalizeText(dateKey);
  const recurrence = eventItem?.recurrence && typeof eventItem.recurrence === "object" ? eventItem.recurrence : {};
  const exceptions = Array.isArray(recurrence.exceptions) ? recurrence.exceptions : [];
  if (!eventItem || !occurrenceDateKey || !exceptions.includes(occurrenceDateKey)) {
    return eventItem;
  }

  return {
    ...eventItem,
    recurrence: {
      ...recurrence,
      exceptions: exceptions.filter((item) => item !== occurrenceDateKey),
    },
  };
};

export const getTeacherHubEventSkippedDateError = (eventItem, dateKey) => {
  const occurrenceDateKey = normalizeText(dateKey);
  if (!occurrenceDateKey) return "Pick a date to skip.";
  if (!eventItem?.recurrence?.enabled) return "Turn on Repeat this event first.";

  const startDate = normalizeText(eventItem.startDate);
  if (startDate && occurrenceDateKey < startDate) {
    return "Skipped dates must be on or after the start date.";
  }

  const until = normalizeText(eventItem.recurrence?.until);
  if (until && occurrenceDateKey > until) {
    return "Skipped dates must be on or before Repeat Until.";
  }

  const exceptions = Array.isArray(eventItem.recurrence?.exceptions) ? eventItem.recurrence.exceptions : [];
  if (exceptions.includes(occurrenceDateKey)) {
    return "That date is already skipped.";
  }

  return "";
};

export const addTeacherHubEventSkippedDate = (eventItem, dateKey) => {
  const error = getTeacherHubEventSkippedDateError(eventItem, dateKey);
  if (error || !eventItem) return eventItem;

  const occurrenceDateKey = normalizeText(dateKey);
  const recurrence = eventItem.recurrence && typeof eventItem.recurrence === "object" ? eventItem.recurrence : {};
  const exceptions = Array.isArray(recurrence.exceptions) ? recurrence.exceptions : [];

  return {
    ...eventItem,
    recurrence: {
      ...recurrence,
      exceptions: Array.from(new Set([...exceptions, occurrenceDateKey])).sort(),
    },
  };
};

export const buildTeacherHubEventSkipOccurrenceMessage = (eventItem, dateKey) => {
  const title = normalizeText(eventItem?.title) || "this event";
  const dateLabel = formatTeacherHubEventDateRange({ startDate: dateKey, endDate: dateKey });
  return `Skip "${title}" on ${dateLabel}? The rest of the series stays.`;
};

export const formatTeacherHubEventDateRange = (eventItemOrOccurrence, options = {}) => {
  const startDateKey = normalizeText(eventItemOrOccurrence?.startDate);
  const endDateKey = normalizeText(eventItemOrOccurrence?.endDate || startDateKey);
  if (!startDateKey) return "No date";

  const locale = options.locale || "en-US";
  const startDate = dateKeyToDate(startDateKey);
  const endDate = dateKeyToDate(endDateKey);
  if (!startDate || !endDate) return startDateKey;

  const formatOptions = options.short
    ? { month: "short", day: "numeric" }
    : { weekday: "short", month: "short", day: "numeric", year: "numeric" };

  if (startDateKey === endDateKey) {
    return startDate.toLocaleDateString(locale, formatOptions);
  }

  return `${startDate.toLocaleDateString(locale, formatOptions)} - ${endDate.toLocaleDateString(locale, formatOptions)}`;
};

export const formatTeacherHubEventTimeRange = (eventItemOrOccurrence, options = {}) => {
  const timeFormat = options.timeFormat;
  if (eventItemOrOccurrence?.allDay || (!eventItemOrOccurrence?.startTime && !eventItemOrOccurrence?.endTime)) {
    return "All day";
  }
  if (eventItemOrOccurrence?.startTime && eventItemOrOccurrence?.endTime) {
    return formatTimeRange(eventItemOrOccurrence.startTime, eventItemOrOccurrence.endTime, timeFormat);
  }
  const singleTime = eventItemOrOccurrence?.startTime || eventItemOrOccurrence?.endTime;
  if (!singleTime) return "All day";
  return formatTimeFromHHmm(singleTime, timeFormat);
};

export const getTeacherHubEventSummaryBits = (eventItem, options = {}) => {
  let classLabel = "School-wide";
  if (eventItem.scope === "personal") {
    classLabel = "Personal";
  } else if (eventItem.scope === "class") {
    const classIds = getTeacherHubEventClassIds(eventItem);
    const classNames = classIds
      .map((classId) => options.classMap?.get(classId)?.className || "")
      .filter(Boolean);
    if (classNames.length === 0) {
      classLabel = classIds.length > 1 ? `${classIds.length} classes` : "Specific class";
    } else if (classNames.length === 1) {
      classLabel = classNames[0];
    } else if (classNames.length <= 3) {
      classLabel = classNames.join(", ");
    } else {
      classLabel = `${classNames.slice(0, 2).join(", ")} +${classNames.length - 2} more`;
    }
  }
  const categoryLabel = normalizeText(eventItem.category);
  const statusLabel = eventItem.status !== "active"
    ? TEACHER_HUB_EVENT_STATUSES.find((option) => option.value === eventItem.status)?.label || eventItem.status
    : "";
  const linkedSchedule = eventItem.scheduleReviewHint?.scheduleTemplateId
    ? options.scheduleTemplatesById?.get(eventItem.scheduleReviewHint.scheduleTemplateId)?.label
      || options.scheduleTemplatesById?.get(eventItem.scheduleReviewHint.scheduleTemplateId)?.name
      || "Schedule review"
    : "";
  return [classLabel, categoryLabel, linkedSchedule, statusLabel].filter(Boolean);
};

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const buildMarqueeOccurrenceLabel = (occurrence, referenceDate) => {
  const isActiveOnReferenceDate =
    occurrence.startDate <= referenceDate
    && occurrence.endDate >= referenceDate;

  if (isActiveOnReferenceDate) {
    return occurrence.startDate === occurrence.endDate
      ? `Today: ${occurrence.title}`
      : `Happening now: ${occurrence.title} through ${formatTeacherHubEventDateRange(occurrence, { short: true })}`;
  }

  return `Upcoming: ${occurrence.title} on ${formatTeacherHubEventDateRange(occurrence, { short: true })}`;
};

export const buildTeacherHubEventMarqueeAnnouncements = (events = [], options = {}) => {
  if (options.enabled === false) {
    return [];
  }

  const referenceDate = normalizeText(options.referenceDate || getTodayDateKey());
  const upcomingLimit = Math.min(5, Math.max(1, Number(options.limit) || 2));
  const includePersonal = options.isShareLink ? false : options.includePersonal === true;
  const horizonEnd = normalizeText(
    options.upcomingHorizonEnd
    || options.rangeEnd
    || addDays(referenceDate, 28)
  );
  const occurrences = getTeacherHubEventOccurrences(events, {
    ...options,
    includePersonal,
    rangeStart: referenceDate,
    rangeEnd: horizonEnd,
    statuses: ["active"],
  });

  const activeToday = occurrences.filter(
    (occurrence) => occurrence.startDate <= referenceDate && occurrence.endDate >= referenceDate
  );
  const messages = [];
  const seenOccurrenceKeys = new Set();
  const showActiveOnDate = options.showActiveOnDate !== false;
  const showUpcomingEvents = options.showUpcomingEvents !== false
    && options.showNextUpcoming !== false;

  const pushOccurrenceMessage = (occurrence, prefix) => {
    const occurrenceKey = `${occurrence.eventId}-${occurrence.startDate}`;
    if (seenOccurrenceKeys.has(occurrenceKey)) return false;

    const label = buildMarqueeOccurrenceLabel(occurrence, referenceDate);
    seenOccurrenceKeys.add(occurrenceKey);
    messages.push({
      id: `teacher-hub-event-${prefix}-${occurrence.eventId}-${occurrence.startDate}`,
      html: `<span>${escapeHtml(label)}</span>`,
      text: label,
    });
    return true;
  };

  if (showActiveOnDate) {
    activeToday.forEach((occurrence) => {
      pushOccurrenceMessage(occurrence, "active");
    });
  }

  if (showUpcomingEvents) {
    const upcomingOccurrences = occurrences
      .filter((occurrence) => occurrence.startDate <= horizonEnd && occurrence.endDate >= referenceDate)
      .sort((leftOccurrence, rightOccurrence) => {
        const leftKey = `${leftOccurrence.startDate} ${leftOccurrence.startTime || "00:00"} ${leftOccurrence.title || ""}`;
        const rightKey = `${rightOccurrence.startDate} ${rightOccurrence.startTime || "00:00"} ${rightOccurrence.title || ""}`;
        return leftKey.localeCompare(rightKey);
      });

    let upcomingCount = 0;
    upcomingOccurrences.forEach((occurrence) => {
      if (upcomingCount >= upcomingLimit) return;
      if (pushOccurrenceMessage(occurrence, "upcoming")) {
        upcomingCount += 1;
      }
    });
  }

  return messages;
};

export const resolveTeacherHubCountdownTarget = (contentData = {}, events = [], options = {}) => {
  if (contentData?.eventSource !== "teacherHub") {
    return {
      eventName: contentData?.eventName || "",
      eventDate: contentData?.eventDate || "",
      source: "manual",
      resolvedEventId: "",
    };
  }

  const referenceDate = normalizeText(options.referenceDate || getTodayDateKey());
  const rangeEnd = addDays(referenceDate, options.horizonDays || 365);
  const selectionMode = contentData?.teacherHubSelectionMode === "manual" ? "manual" : "nextUpcoming";
  const matchedOccurrences = getTeacherHubEventOccurrences(events, {
    ...options,
    rangeStart: referenceDate,
    rangeEnd,
    statuses: ["active"],
  }).filter((occurrence) => {
    if (selectionMode === "manual" && contentData?.teacherHubEventId) {
      return occurrence.eventId === contentData.teacherHubEventId;
    }
    return true;
  });

  const match = matchedOccurrences[0] || null;
  if (!match) {
    return {
      eventName: "",
      eventDate: "",
      source: "teacherHub",
      resolvedEventId: "",
      resolutionLabel: "No matching event",
    };
  }

  return {
    eventName: match.title,
    eventDate: match.startDate,
    source: "teacherHub",
    resolvedEventId: match.eventId,
    resolutionLabel: selectionMode === "manual" ? "Pinned from Events Manager" : "Next event from Events Manager",
    occurrence: match,
    event: match.event,
  };
};

export const resolveTeacherHubCountdownTargets = (contentData = {}, events = [], options = {}) => {
  if (contentData?.eventSource !== "teacherHub") {
    return [];
  }

  const referenceDate = normalizeText(options.referenceDate || getTodayDateKey());
  const rangeEnd = addDays(referenceDate, options.horizonDays || 365);
  const eventCount = normalizeCountdownEventCount(contentData);
  const selectionMode = contentData?.teacherHubSelectionMode === "manual" ? "manual" : "nextUpcoming";
  const occurrences = getTeacherHubEventOccurrences(events, {
    ...options,
    rangeStart: referenceDate,
    rangeEnd,
    statuses: ["active"],
  });

  if (selectionMode !== "manual") {
    return occurrences.slice(0, eventCount).map((occurrence, index) => ({
      eventName: occurrence.title,
      eventDate: occurrence.startDate,
      source: "teacherHub",
      resolvedEventId: occurrence.eventId,
      resolutionLabel: "Next event from Events Manager",
      occurrence,
      event: occurrence.event,
      slotIndex: index,
    }));
  }

  const eventIds = normalizeTeacherHubCountdownEventIds(contentData).slice(0, eventCount);
  const displayLabels = normalizeTeacherHubCountdownDisplayLabels(contentData);
  const targets = [];
  const usedOccurrenceKeys = new Set();

  eventIds.forEach((eventId, index) => {
    if (!eventId) return;
    const match = occurrences.find((occurrence) => {
      const occurrenceKey = `${occurrence.eventId}-${occurrence.startDate}`;
      return occurrence.eventId === eventId && !usedOccurrenceKeys.has(occurrenceKey);
    });
    if (!match) return;

    usedOccurrenceKeys.add(`${match.eventId}-${match.startDate}`);
    targets.push({
      eventName: displayLabels[index] || match.title,
      eventDate: match.startDate,
      source: "teacherHub",
      resolvedEventId: match.eventId,
      resolutionLabel: "Pinned from Events Manager",
      occurrence: match,
      event: match.event,
      slotIndex: index,
    });
  });

  return targets;
};

export const buildTeacherHubScheduleReviewState = (eventItem, options = {}) => {
  const normalizedEvent = normalizeTeacherHubEvent(eventItem, options);
  const referenceDate = normalizeText(options.referenceDate || getTodayDateKey());
  const occurrence = getTeacherHubEventOccurrences([normalizedEvent], {
    ...options,
    rangeStart: referenceDate,
    rangeEnd: addDays(referenceDate, 365),
    includeSchool: true,
    includeClass: true,
    includePersonal: true,
    statuses: ["active", "cancelled", "hidden"],
  })[0] || {
    eventId: normalizedEvent.id,
    startDate: normalizedEvent.startDate,
    endDate: normalizedEvent.endDate,
  };

  return {
    openScheduleId: normalizedEvent.scheduleReviewHint?.scheduleTemplateId || "",
    focusClassId: getTeacherHubEventClassIds(normalizedEvent)[0] || "",
    reviewDateKeys: enumerateDateKeys(occurrence.startDate, occurrence.endDate),
    reviewSourceEventId: normalizedEvent.id,
    reviewMessage: normalizedEvent.title ? `Review the schedule around "${normalizedEvent.title}".` : "Review the linked schedule.",
  };
};
